import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/20/solid';
import { Transition, Dialog } from '@headlessui/react';
import dayjs from 'dayjs';

import { NotReadyToChatIcon, ReadyToChatIcon } from 'src/assets/icons';

import { Spinner } from 'src/ui/components';

import { useGetAlertRevisions } from '../api';

import { classNames } from 'src/utils/className';
import {
  getPtTimeZoneDate,
  getPtTimeIn24hr,
  getTimezoneFromAbbreviation,
} from 'src/utils/getPTZone';
import { calculateAgeAsString } from 'src/utils/common';
import * as utilityFn from 'src/utils/common';

import { PhoneType } from '../types/accountAlerts';
import { AlertRevisonListType } from '../types/accountAlerts';
import { PersonAlertListType } from '../types/personAlerts';
import { CountryObj } from 'src/types/countryType';

type AlertHistoryPropsType = {
  isAlertHistoryOpen: boolean;
  setIsAlertHistoryOpen: (
    value: boolean | ((prevState: boolean) => boolean),
  ) => void;
  alertRevisions: AlertRevisonListType[] | undefined;
  selectedAlert: PersonAlertListType | undefined;
  setSelectedAlert: any;
  countryList: Array<CountryObj> | undefined;
};

export function AlertHistory({
  isAlertHistoryOpen,
  setIsAlertHistoryOpen,
  alertRevisions,
  selectedAlert,
  setSelectedAlert,
  countryList,
}: AlertHistoryPropsType) {
  const [selectedRevision, setSelectedRevision] = useState<any>();

  const { data: revisionDetails, isLoading: isRevisionDetailsLoading } =
    useGetAlertRevisions(
      selectedAlert?.Person?.AccountId,
      selectedAlert?.Id,
      selectedRevision?.Id,
    );

  useEffect(() => {
    if (alertRevisions) setSelectedRevision(alertRevisions[0]);
  }, [alertRevisions]);

  const sortedRevisions = useMemo(() => {
    return alertRevisions?.sort(
      (a: any, b: any) =>
        new Date(b.UpdatedOn).getTime() - new Date(a.UpdatedOn).getTime(),
    );
  }, [alertRevisions]);

  const countryCode = useMemo(() => {
    if (countryList) {
      return utilityFn.countryOptions(countryList);
    }
  }, [countryList]);

  function onModalClose() {
    setIsAlertHistoryOpen(false);
    setSelectedRevision(undefined);
    setSelectedAlert(undefined);
  }

  function getCountryCode(countryId: number | undefined | null) {
    if (countryId)
      return countryList?.find((item: any) => item?.Id === countryId)?.ISDCode;
    else return '+1';
  }

  function getFormattedNumber(phoneInfo: PhoneType | undefined | null) {
    if (phoneInfo && phoneInfo?.Number) {
      const isUsFormat = utilityFn.hasUsFormat(
        countryCode,
        phoneInfo?.CountryId || 0,
      );
      const phonenumber = utilityFn.formatPhoneNumber(
        phoneInfo?.Number,
        isUsFormat,
      );
      return phonenumber;
    }
  }

  function getResetInterval() {
    const isoString =
      revisionDetails?.PersonCallLimitRevision?.ResetIntervalOffset;
    const timezone =
      revisionDetails?.PersonCallLimitRevision?.TimeZone?.Abbreviation;
    const timezoneAbb = getTimezoneFromAbbreviation(timezone || '');
    const timeOnly = dayjs(isoString).tz(timezoneAbb).format('hh:mm A');
    return timeOnly;
  }

  function renderRevisionList(item: AlertRevisonListType) {
    return (
      <div
        key={item?.Id}
        className={classNames(
          'cursor-pointer rounded-md border px-2 py-1 !leading-none',
          selectedRevision?.Id === item.Id
            ? 'border-primary-400 bg-primary-100  dark:border-primary-800/50 dark:bg-primary-900' // active styles
            : 'border-gray-200 bg-white dark:border-neutral-700/50 dark:bg-neutral-700/60', // default styles
        )}
        onClick={() => setSelectedRevision(item)}
      >
        <p className="text-sm font-semibold text-gray-900 dark:text-white">
          {`${getPtTimeZoneDate(item.UpdatedOn)}  ${getPtTimeIn24hr(
            item.UpdatedOn,
          )}`}
        </p>
        <p className="text-xs text-gray-600 dark:text-white/50">
          {`Edited by: ${item.UpdatedBy}`}
        </p>
        {item.UpdatedFromAlertId !== item.PersonAlertId ? (
          <p className="text-xs text-gray-600 dark:text-white/50">
            {`Updated due to change in Alert ID: `}
            <span className="font-bold">{item.UpdatedFromAlertId}</span>
          </p>
        ) : null}
      </div>
    );
  }

  function renderAccountSection() {
    return (
      <dl className="border dark:border-dark-medium">
        <h3 className="sticky -top-px border-b bg-gray-100 px-3 py-2 text-xl font-semibold dark:border-dark-medium dark:bg-neutral-900">
          Account
        </h3>
        <div className="flex gap-2 border-b dark:border-dark-medium">
          <dt className="w-1/3 p-2 text-sm font-medium">Account name</dt>
          <dd className="w-4/6 border-l border-l-light-light p-2 text-sm dark:border-l-dark-medium">
            {selectedAlert?.Person?.Account?.AccountNameAndNumber}
          </dd>
        </div>
        <div className="flex gap-2 border-b dark:border-dark-medium">
          <dt className="w-1/3 p-2 text-sm font-medium">CID</dt>
          <dd className="w-4/6 border-l border-l-light-light p-2 text-sm dark:border-l-dark-medium">
            {selectedAlert?.Person?.Account?.CID}
          </dd>
        </div>
        <div className="flex gap-2 border-b dark:border-dark-medium">
          <dt className="w-1/3 p-2 text-sm font-medium">SID</dt>
          <dd className="w-4/6 border-l border-l-light-light p-2 text-sm dark:border-l-dark-medium">
            {selectedAlert?.Person?.Account?.SID}
          </dd>
        </div>
        <div className="flex gap-2 border-b dark:border-dark-medium">
          <dt className="w-1/3 p-2 text-sm font-medium">PID</dt>
          <dd className="w-4/6 border-l border-l-light-light p-2 text-sm dark:border-l-dark-medium">
            {selectedAlert?.Person?.Account?.PID}
          </dd>
        </div>
      </dl>
    );
  }

  function renderPhoneNumber(
    phoneInfo: PhoneType | undefined | null,
    label: string,
  ) {
    return (
      <div className="flex gap-2 border-b dark:border-dark-medium">
        <dt className="w-1/3 p-2 text-sm font-medium">{label}</dt>
        <dd className="w-4/6 border-l border-l-light-light p-2 text-sm dark:border-l-dark-medium">
          <p>
            {phoneInfo?.PhoneType?.Description && (
              <label className="text-sm font-medium">
                {phoneInfo.PhoneType.Description}:
              </label>
            )}
            {getCountryCode(phoneInfo?.CountryId)} {''}{' '}
            {getFormattedNumber(phoneInfo)}
            {phoneInfo?.OkToLeaveMessage === true ? (
              <ReadyToChatIcon className="ms-1 text-green-700 dark:text-green-400" />
            ) : (
              phoneInfo?.OkToLeaveMessage === false && (
                <NotReadyToChatIcon className="ms-1 text-red-700 dark:text-red-400" />
              )
            )}
          </p>
          {phoneInfo?.Comments && phoneInfo?.Comments?.length > 0 && (
            <p>
              <label className="text-sm font-medium">Comments: </label>
              {phoneInfo?.Comments}
            </p>
          )}
        </dd>
      </div>
    );
  }

  function renderAlertInfo() {
    return (
      <dl className="border dark:border-dark-medium">
        <h3 className="sticky -top-px border-b bg-gray-100 px-3 py-2 text-xl font-semibold dark:border-dark-medium dark:bg-neutral-900">
          Alert information
        </h3>
        <div className="flex gap-2 border-b dark:border-dark-medium">
          <dt className="w-1/3 p-2 text-sm font-medium">
            Counselor or Case manager (if applicable)
          </dt>
          <dd className="w-4/6 border-l border-l-light-light p-2 text-sm dark:border-l-dark-medium">
            {revisionDetails?.CounselorOrCaseManager}
          </dd>
        </div>
        <div className="flex gap-2 border-b dark:border-dark-medium">
          <dt className="w-1/3 p-2 text-sm font-medium">
            Special program participant
          </dt>
          <dd className="w-4/6 border-l border-l-light-light p-2 text-sm dark:border-l-dark-medium">
            {revisionDetails?.SpecialProgramsParticipant}
          </dd>
        </div>
        <div className="flex gap-2 border-b dark:border-dark-medium">
          <dt className="w-1/3 p-2 text-sm font-medium">
            Situation description
          </dt>
          <dd className="w-4/6 border-l border-l-light-light p-2 text-sm dark:border-l-dark-medium">
            <div
              className="prose max-w-full dark:prose-invert"
              dangerouslySetInnerHTML={utilityFn.createMarkup(
                revisionDetails?.SituationDescription || '',
              )}
            ></div>
          </dd>
        </div>
        <div className="flex gap-2 border-b dark:border-dark-medium">
          <dt className="w-1/3 p-2 text-sm font-medium">
            Telephone intervention description
          </dt>
          <dd className="w-4/6 border-l border-l-light-light p-2 text-sm dark:border-l-dark-medium">
            <div
              className="prose max-w-full dark:prose-invert"
              dangerouslySetInnerHTML={utilityFn.createMarkup(
                revisionDetails?.TelephoneInterventionDescription || '',
              )}
            ></div>
          </dd>
        </div>
        <div className="flex gap-2 border-b dark:border-dark-medium">
          <dt className="w-1/3 p-2 text-sm font-medium">Contact limit</dt>
          <dd className="w-4/6 border-l border-l-light-light p-2 text-sm dark:border-l-dark-medium">
            <p>
              <label className="text-sm font-medium">
                Is contact limit set?:{' '}
              </label>

              {revisionDetails?.PersonCallLimitRevision === null ? (
                <span>No</span>
              ) : (
                <span>Yes</span>
              )}
            </p>
            {revisionDetails &&
              revisionDetails?.PersonCallLimitRevision !== null && (
                <>
                  <p>
                    <label className="text-sm font-medium">Calls: </label>
                    {revisionDetails?.PersonCallLimitRevision?.MaxCallCount}
                  </p>
                  <p>
                    <label className="text-sm font-medium">Reset time: </label>
                    {getResetInterval()}
                  </p>
                  <p>
                    <label className="text-sm font-medium">Timezone: </label>
                    {
                      revisionDetails?.PersonCallLimitRevision?.TimeZone
                        ?.Abbreviation
                    }
                  </p>
                </>
              )}
          </dd>
        </div>
      </dl>
    );
  }

  function renderPersonInfo() {
    return (
      <dl className="border dark:border-dark-medium">
        <h3 className="sticky -top-px border-b bg-gray-100 px-3 py-2 text-xl font-semibold dark:border-dark-medium dark:bg-neutral-900">
          Person Information
        </h3>
        <div className="flex gap-2 border-b dark:border-dark-medium">
          <dt className="w-1/3 p-2 text-sm font-medium">Alert ID</dt>
          <dd className="w-4/6 border-l border-l-light-light p-2 text-sm dark:border-l-dark-medium">
            {revisionDetails?.PersonAlertId}
          </dd>
        </div>
        <div className="flex gap-2 border-b dark:border-dark-medium">
          <dt className="w-1/3 p-2 text-sm font-medium">Entered by</dt>
          <dd className="w-4/6 border-l border-l-light-light p-2 text-sm dark:border-l-dark-medium">
            {revisionDetails?.UpdatedBy}
          </dd>
        </div>
        <div className="flex gap-2 border-b dark:border-dark-medium">
          <dt className="w-1/3 p-2 text-sm font-medium">Entered date</dt>
          <dd className="w-4/6 border-l border-l-light-light p-2 text-sm dark:border-l-dark-medium">
            {revisionDetails?.UpdatedOn !== null
              ? dayjs(revisionDetails?.UpdatedOn).format('MM/DD/YYYY H:mm:ss')
              : ''}
          </dd>
        </div>
        <div className="flex gap-2 border-b dark:border-dark-medium">
          <dt className="w-1/3 p-2 text-sm font-medium">
            Start date - End date
          </dt>
          <dd className="w-4/6 border-l border-l-light-light p-2 text-sm dark:border-l-dark-medium">
            {revisionDetails?.StartDate !== null
              ? dayjs(revisionDetails?.StartDate).format('MM/DD/YYYY HH:mm:ss')
              : ''}{' '}
            -{' '}
            {revisionDetails?.EndDate !== null
              ? dayjs(revisionDetails?.EndDate).format('MM/DD/YYYY HH:mm:ss')
              : ''}
          </dd>
        </div>
        <div className="flex gap-2 border-b dark:border-dark-medium">
          <dt className="w-1/3 p-2 text-sm font-medium">Reason for Alert</dt>
          <dd className="w-4/6 border-l border-l-light-light p-2 text-sm dark:border-l-dark-medium">
            {revisionDetails?.Reasons}
          </dd>
        </div>
        <div className="flex gap-2 border-b dark:border-dark-medium">
          <dt className="w-1/3 p-2 text-sm font-medium">
            {`Person alert limiter - [${revisionDetails?.LimitCategory}]`}
          </dt>
          <dd className="w-4/6 border-l border-l-light-light p-2 text-sm dark:border-l-dark-medium">
            {revisionDetails?.PersonAlertLimiterValues}
          </dd>
        </div>
        <div className="flex gap-2 border-b dark:border-dark-medium">
          <dt className="w-1/3 p-2 text-sm font-medium">First name</dt>
          <dd className="w-4/6 border-l border-l-light-light p-2 text-sm dark:border-l-dark-medium">
            {revisionDetails?.PersonDemographicsRevision?.FirstName}
          </dd>
        </div>
        <div className="flex gap-2 border-b dark:border-dark-medium">
          <dt className="w-1/3 p-2 text-sm font-medium">Last name</dt>
          <dd className="w-4/6 border-l border-l-light-light p-2 text-sm dark:border-l-dark-medium">
            {revisionDetails?.PersonDemographicsRevision?.LastName}
          </dd>
        </div>
        <div className="flex gap-2 border-b dark:border-dark-medium">
          <dt className="w-1/3 p-2 text-sm font-medium">Alternate name</dt>
          <dd className="w-4/6 border-l border-l-light-light p-2 text-sm dark:border-l-dark-medium">
            {revisionDetails?.PersonDemographicsRevision?.PreferredName}
          </dd>
        </div>
        <div className="flex gap-2 border-b dark:border-dark-medium">
          <dt className="w-1/3 p-2 text-sm font-medium">Date of Birth</dt>
          <dd className="w-4/6 border-l border-l-light-light p-2 text-sm dark:border-l-dark-medium">
            {revisionDetails?.PersonDemographicsRevision?.Birthdate !==
              null && (
              <p>
                <label className="text-sm font-medium">Birth date: </label>
                {dayjs(
                  revisionDetails?.PersonDemographicsRevision?.Birthdate,
                ).format('MM/DD/YYYY')}
              </p>
            )}

            {revisionDetails?.PersonDemographicsRevision?.Age !== null &&
            revisionDetails?.PersonDemographicsRevision?.Age !== '' ? (
              <div>
                <label className="text-sm font-medium">Age: </label>
                <span>
                  {revisionDetails?.PersonDemographicsRevision?.Age !== null
                    ? revisionDetails?.PersonDemographicsRevision?.Age
                    : calculateAgeAsString(
                        revisionDetails?.PersonDemographicsRevision
                          ?.Birthdate || '',
                      )}
                </span>
              </div>
            ) : null}
          </dd>
        </div>
        <div className="flex gap-2 border-b dark:border-dark-medium">
          <dt className="w-1/3 p-2 text-sm font-medium">Address</dt>
          <dd className="w-4/6 border-l border-l-light-light p-2 text-sm dark:border-l-dark-medium">
            {revisionDetails?.PersonDemographicsRevision?.PrimaryAddress !==
            null ? (
              <>
                <p>
                  <span className="font-semibold">Street 1: </span>
                  {
                    revisionDetails?.PersonDemographicsRevision?.PrimaryAddress
                      ?.Street1
                  }
                </p>
                <p>
                  <span className="font-semibold">Street 2: </span>
                  {
                    revisionDetails?.PersonDemographicsRevision?.PrimaryAddress
                      ?.Street2
                  }
                </p>
                <p>
                  {' '}
                  <span className="font-semibold">Street 3: </span>
                  {
                    revisionDetails?.PersonDemographicsRevision?.PrimaryAddress
                      ?.Street3
                  }
                </p>
                <p>
                  {' '}
                  <span className="font-semibold">City: </span>
                  {
                    revisionDetails?.PersonDemographicsRevision?.PrimaryAddress
                      ?.City
                  }
                </p>
                <p>
                  {' '}
                  <span className="font-semibold">State/Province: </span>
                  {
                    revisionDetails?.PersonDemographicsRevision?.PrimaryAddress
                      ?.StateProvince?.Name
                  }
                </p>
                <p>
                  <span className="font-semibold">Zip/Postal code: </span>
                  {
                    revisionDetails?.PersonDemographicsRevision?.PrimaryAddress
                      ?.ZipPostalCode
                  }
                </p>
                <p>
                  <span className="font-semibold">Country: </span>
                  {
                    revisionDetails?.PersonDemographicsRevision?.PrimaryAddress
                      ?.Country?.Name
                  }
                </p>
                <p>
                  <span className="font-semibold">County: </span>
                  {
                    revisionDetails?.PersonDemographicsRevision?.PrimaryAddress
                      ?.County
                  }
                </p>
              </>
            ) : null}
          </dd>
        </div>
        {renderPhoneNumber(
          revisionDetails?.PersonDemographicsRevision?.PrimaryPhone,
          'Primary phone',
        )}

        {renderPhoneNumber(
          revisionDetails?.PersonDemographicsRevision?.SecondaryPhone,
          'Secondary phone',
        )}
      </dl>
    );
  }

  return (
    <Transition appear show={isAlertHistoryOpen} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-10"
        onClose={() => {
          onModalClose();
        }}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="pr-dialog__backdrop" />
        </Transition.Child>

        <div className="pr-dialog__wrapper">
          <div className="pr-dialog__panel__container sm:!grid-rows-[1fr_auto_1fr]">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                style={{ maxHeight: 'calc(100svh - 90px)' }}
                className="pr-dialog__panel flex flex-col sm:!max-w-5xl"
              >
                <div className="flex items-center gap-3 border-b p-4 dark:border-dark-medium">
                  <Dialog.Title
                    as="h3"
                    className="grow truncate text-lg font-medium leading-6 text-gray-900 dark:text-white"
                  >
                    Alert history
                  </Dialog.Title>

                  <button
                    type="button"
                    className=" text-gray-400 hover:text-gray-500 sm:right-4 sm:top-4"
                    onClick={() => {
                      onModalClose();
                    }}
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>

                <div className="relative min-h-0 grow">
                  <div className="grid h-full grid-cols-[minmax(160px,25%)_1fr]">
                    <div className="space-y-2 overflow-y-scroll scroll-smooth bg-gray-100 p-2 dark:bg-neutral-800">
                      {sortedRevisions &&
                        sortedRevisions?.map((item: any) =>
                          renderRevisionList(item),
                        )}
                    </div>
                    <div className="my-4 overflow-y-scroll scroll-smooth">
                      <div className="min-w-80 space-y-4 px-4">
                        {renderAccountSection()}
                        {renderPersonInfo()}
                        {renderAlertInfo()}
                      </div>
                    </div>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
        {isRevisionDetailsLoading && (
          <div className="fixed inset-0 z-20 grid place-items-center">
            <Spinner size="large" />
          </div>
        )}
      </Dialog>
    </Transition>
  );
}
